import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Get, Patch, Post } from "libs/services";
import { STORES } from "libs/config";
import { setLoading } from "./loader";
import {
  StoreResponse,
  StoreResponsePayload,
} from "src/common/types/response/store";
import { BaseResponse } from "src/common/types/response/BasePaginationResponse";
import { openToast } from "./toast";
import translate from "src/common/libs/locale";
import { MERCHANT } from "src/common/libs/config/merchant.config";
import { updateMerchantImage } from "./auth";
import { getFileFromUri } from "src/common/libs/utils/image";
import { reportLog } from "src/common/libs/utils/logs";

interface ItemsState {
  data?: {
    [key: string]: StoreResponse;
  };
  loading: boolean;
  serviceLoading: boolean;
  serviceable: boolean;
}

export const checkServiceability = createAsyncThunk(
  "store/checkServiceability",
  async ({ userLocation }: { userLocation: [number, number] }, thunkAPI) => {
    try {
      // thunkAPI.dispatch(setLoading(true));
      const response = await Post<StoreResponse>(`${STORES.serviceable}`, {
        userLocation,
      });
      if (response.data.status) {
        return { data: response.data };
      }
      return null;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/store->checkServiceability",
        body: { userLocation },
      });
      return null;
    } finally {
      // thunkAPI.dispatch(setLoading(false));
    }
  }
);

export const getStoreDetail = createAsyncThunk(
  "store/getStoreDetail",
  async ({ id, guest }: { id: string; guest?: boolean }, thunkAPI) => {
    try {
      const query = {};
      if (guest) {
        query["role"] = "guest";
      }
      const response = await Get<StoreResponse>(
        `${STORES.getDetail}/${id}`,
        query
      );
      if (response.data.status) {
        return { data: response.data, key: id };
      }
      return null;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/store->getStoreDetail",
        body: { id },
      });
      return null;
    } finally {
      thunkAPI.dispatch(setLoading(false));
    }
  }
);

export const updateStore = createAsyncThunk(
  "store/updateStore",
  async ({ body = {} }: { body: any }, thunkAPI) => {
    try {
      const response = await Patch<BaseResponse<StoreResponsePayload>>(
        `${STORES.patch}`,
        body
      );
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/store->updateStore",
        body: { body },
      });
      thunkAPI.dispatch(
        openToast({ message: translate("unableToUpdateStatus") })
      );
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateStoreImage = createAsyncThunk(
  "items/updateStoreImage",
  async ({ id, file }: { id: string; file: string }, thunkAPI) => {
    try {
      const formData = new FormData();
      const imageData = await getFileFromUri(file);
      formData.append("file", imageData);
      formData.append("_id", id);
      const response = await Patch<BaseResponse<any>>(
        MERCHANT.updateImage,
        formData,
        null,
        {
          "content-type": "multipart/form-data",
        }
      );
      thunkAPI.dispatch(updateMerchantImage(file));
      return { data: response.data, id };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/store->updateStoreImage",
        body: { id, file },
      });
      thunkAPI.dispatch(
        openToast({
          message: translate("failedUploadImage") || error.message,
          type: "fail",
        })
      );
      return null;
    }
  }
);

const initialState: ItemsState = {
  data: null,
  loading: false,
  serviceLoading: false,
  serviceable: false,
};

export const storeDetailSlice = createSlice({
  name: "storeDetail",
  initialState,
  reducers: {
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getStoreDetail.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getStoreDetail.fulfilled, (state, action) => {
      state.data = {
        ...state.data,
        [action.payload.key]: action.payload.data,
      };
      state.loading = false;
    });
    builder.addCase(getStoreDetail.rejected, (state, action) => {
      state.loading = false;
    });

    builder.addCase(checkServiceability.pending, (state, action) => {
      state.serviceLoading = true;
    });
    builder.addCase(checkServiceability.fulfilled, (state, action: any) => {
      state.serviceLoading = false;
      state.serviceable = action.payload?.data?.payload?.serviceable;
    });
    builder.addCase(checkServiceability.rejected, (state, action) => {
      state.serviceLoading = false;
    });
  },
});

export const { clearData } = storeDetailSlice.actions;

export default storeDetailSlice.reducer;
